import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck, ScanLine } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { libraryInventoryApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import { formatDate, orDash } from '@/utils/format';
import { AuditStatusBadge, CopyStatusBadge, Field, FindingBadge, Stat } from './shared';

/**
 * Working down a shelf.
 *
 * <p>One input, focused, that a barcode reader types into. Scanning the same
 * book twice records it once, and a book that was not on the list is added as an
 * unexpected find rather than rejected — it is usually the shelf it belongs on
 * that will report it missing.
 *
 * <p>Finishing applies what was found: everything unscanned becomes MISSING,
 * misplaced copies are re-shelved where they were actually found, and copies
 * that had been missing and have now turned up come back into stock. Nothing is
 * deleted and nobody is charged.
 */
export function InventoryAuditPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const manages = can(PERMISSIONS.LIBRARY_INVENTORY_MANAGE);

  const [code, setCode] = useState('');
  const scanner = useRef<HTMLInputElement>(null);

  const audit = useQuery({
    queryKey: queryKeys.libraryInventory.audit(id),
    queryFn: () => libraryInventoryApi.audit(id),
  });

  const inProgress = audit.data?.status === 'IN_PROGRESS';

  useEffect(() => {
    if (inProgress) {
      scanner.current?.focus();
    }
  }, [inProgress]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.libraryInventory.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.library.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.books.all });
  };

  const scan = useMutation({
    mutationFn: (scanned: string) => libraryInventoryApi.scan(id, { code: scanned }),
    onSuccess: (updated) => {
      setCode('');
      queryClient.setQueryData(queryKeys.libraryInventory.audit(id), updated);
      scanner.current?.focus();
    },
    onError: (error) => {
      handleApiError(error);
      setCode('');
      scanner.current?.focus();
    },
  });

  const complete = useMutation({
    mutationFn: () => libraryInventoryApi.complete(id),
    onSuccess: (done) => {
      toast.success(
        done.missing === 0
          ? 'Everything was found'
          : `${done.missing} cop${done.missing === 1 ? 'y' : 'ies'} not found — marked missing, not deleted`,
      );
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const abandon = useMutation({
    mutationFn: (reason: string) => libraryInventoryApi.abandon(id, reason),
    onSuccess: () => {
      toast.info('Count abandoned. Nothing was applied.');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  if (audit.isError) {
    return <ErrorState error={audit.error} onRetry={() => void audit.refetch()} />;
  }
  if (audit.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const data = audit.data;

  return (
    <div>
      <Link
        to={ROUTES.libraryInventory}
        className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted hover:text-accent"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Stock
      </Link>

      <PageHeader
        title={data.name}
        description={`${data.reference} · ${orDash(data.locationPath ?? 'whole library')} · started ${formatDate(data.startedOn)}`}
        icon={<ClipboardCheck className="h-5 w-5" aria-hidden="true" />}
        action={<AuditStatusBadge status={data.status} />}
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <Stat label="Expected" value={data.expectedCount} hint="What should be on the shelves" />
        <Stat label="Found" value={data.seen} tone="success" />
        <Stat
          label="Still to find"
          value={data.pending}
          tone={data.pending > 0 ? 'warning' : 'neutral'}
        />
        <Stat
          label="Not found"
          value={data.missing}
          tone={data.missing > 0 ? 'warning' : 'neutral'}
        />
        <Stat
          label="Unexpected"
          value={data.unexpected}
          hint="Found here, belongs elsewhere"
        />
      </div>

      {inProgress && manages && (
        <Card className="mt-4 p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Scan</h2>
          <p className="mt-0.5 text-xs text-muted">
            Work along the shelf. Scanning the same book twice records it once.
          </p>
          <form
            className="mt-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (code.trim() !== '') {
                scan.mutate(code.trim());
              }
            }}
          >
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">
                Barcode or accession number
              </span>
              <span className="mt-1 flex items-center gap-2 border border-line bg-bg px-3 py-2 focus-within:border-accent">
                <ScanLine className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
                <input
                  ref={scanner}
                  className="w-full bg-transparent text-base text-ink outline-none"
                  value={code}
                  placeholder="Scan a book…"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={scan.isPending}
                  onChange={(event) => setCode(event.target.value)}
                />
                {scan.isPending && <Spinner />}
              </span>
            </label>
          </form>

          <div className="mt-4 flex flex-wrap gap-2 border-t border-line pt-3">
            <Button onClick={() => complete.mutate()} disabled={complete.isPending}>
              Finish the count
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                const reason = window.prompt('Why is the count being abandoned?');
                if (reason !== null && reason.trim() !== '') {
                  abandon.mutate(reason.trim());
                }
              }}
            >
              Abandon it
            </Button>
            <p className="w-full text-xs text-muted">
              Finishing marks everything unscanned as missing. Abandoning concludes nothing — an
              interrupted count has not searched the shelves.
            </p>
          </div>
        </Card>
      )}

      <Card className="mt-4">
        <div className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            What the count found
          </h2>
        </div>
        {data.items.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-muted">
            Nothing recorded yet. The lines appear as the shelf is scanned.
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {data.items.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center gap-3 px-5 py-2">
                <span className="min-w-0 flex-1">
                  <span className="block font-mono text-sm text-ink">{item.accessionNumber}</span>
                  {item.scannedAt !== undefined && (
                    <span className="block text-[11px] text-subtle">
                      seen {formatDate(item.scannedAt)}
                    </span>
                  )}
                </span>
                <span className="text-[11px] text-subtle">was</span>
                <CopyStatusBadge status={item.expectedStatus} />
                <FindingBadge finding={item.finding} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      {data.status !== 'IN_PROGRESS' && (
        <Card className="mt-4 p-4">
          <dl>
            <Field label="Finished">
              {data.completedOn === undefined ? '—' : formatDate(data.completedOn)}
            </Field>
            <Field label="Misplaced">{data.misplaced}</Field>
            {data.notes !== undefined && <Field label="Notes">{data.notes}</Field>}
          </dl>
        </Card>
      )}
    </div>
  );
}
